import { useAxios, IResponse } from '../useAxios';
import {
  ICreateCampaignPayload, IUpdateCampaignPayload,
  CampaignLeadStatus, IExecuteStepPayload, ISenderSignature,
  ILinkedInLoginFields, SuppressionType, SuppressionListScope,
} from '../../../interfaces/models/campaign';
import { IConnectionsQueryParams } from '../connections/useConnectionsRequest';
import { ICampaignVariableFallbacks } from '../../../utils/campaignTemplateVariables';

export type IUpdateProxyConfigPayload =
  | { liEmail: string; liPassword: string; country?: string }      // connect via credentials
  | ({ accessToken: string; premiumAccessToken?: string; userAgent: string; country?: string }) // connect via the open browser session
  | { reconnectAccountId: string; liEmail: string; liPassword: string; country?: string } // reconnect a disconnected account
  | ({ reconnectAccountId: string; accessToken: string; premiumAccessToken?: string; userAgent: string; country?: string }) // reconnect via the same browser session
  | { unipileAccountIds: string[] }                                 // set/replace entire pool
  | { addAccountIds: string[] }                                     // add existing workspace accounts (reuse)
  | { removeAccountId: string }                                     // remove one account (this campaign only)
  | { disconnectAccountId: string }                                 // remove; delete on provider if last campaign
  | { accountId: string; checkpointCode?: string };                 // solve code checkpoint or confirm in-app approval

/**
 * Mailbox connect form. Host/port are optional — Unipile resolves them from the
 * domain for common providers; they are only needed for self-hosted mail.
 */
export interface IConnectMailboxPayload {
  email: string;
  password: string;
  imapHost?: string;
  imapPort?: string;
  smtpHost?: string;
  smtpPort?: string;
}

/** Live provider-side details for one account in the campaign pool */
export interface IProxyAccountDetails {
  accountId: string;
  name: string | null;
  /** LinkedIn login email, from the workspace's account mapping (null if unmapped). */
  email?: string | null;
  status: string;   // 'connected' | 'pending' | 'credentials' | 'not_found' | 'error' | ...
  pending: boolean;
  /** Paid LinkedIn products on the session: 'premium' | 'sales_navigator' | 'recruiter'. */
  premiumFeatures?: string[];
  /** Remaining InMail credits (null when unknown / not connected). */
  inMailCredits?: {
    classic: number | null;
    salesNavigator: number | null;
    recruiter?: number | null;
    error?: string | null;
  } | null;
}

/**
 * "Leads with this analytics activity in this period" — narrows a lead listing and any
 * select-all bulk action. No dates means all time.
 */
export interface ILeadActivityQuery {
  activity?: string;
  from?: string;
  to?: string;
  tz?: string;
}

/** Filters shared by the Performance views. Dates are calendar days, both inclusive. */
export interface IPerformanceQuery {
  from: string;
  to: string;
  tz: string;
  campaignIds: string[];
  identityIds: string[];
}

const performanceParams = ({ campaignIds, identityIds, ...rest }: IPerformanceQuery) => ({
  ...rest,
  ...(campaignIds.length ? { campaignIds: campaignIds.join(',') } : {}),
  ...(identityIds.length ? { identityIds: identityIds.join(',') } : {}),
});

/** An existing workspace account that can be reused (added to the campaign) */
export interface IWorkspaceAccount {
  accountId: string;
  name: string | null;
  /** LinkedIn login email, from the workspace's account mapping (null if unmapped). */
  email?: string | null;
  status: string;
  pending: boolean;
  inThisCampaign: boolean;
  usedBy: string[];   // names of campaigns currently using it
}

/** Full contacts filter payload — same shape as GET /profiles/v1 query params */
export type IContactSearchPayload = IConnectionsQueryParams & {
  /** @deprecated legacy tag filter — prefer `tags` */
  tagIds?: string[];
  /** @deprecated legacy autotag filter — prefer `autotagsFilter` */
  autoTagTypes?: string[];
  limit?: number;
};

export interface ICsvLeadRow {
  /** Classic vanity slug when known. Optional for Sales Nav list imports (ACw linkedinId). */
  publicIdentifier?: string;
  linkedinId?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  company?: string;
  location?: string;
  linkedinUrl?: string;
  imageUrl?: string;
  /** Address taken straight from the file — costs no enrichment credits. */
  email?: string;
  emailType?: 'business' | 'private';
  /** Employer's domain or website — matched against the workspace suppression list. */
  companyDomain?: string;
}

export const useCampaignsRequest = () => {
  const { sendRequest } = useAxios();
  const route = '/campaigns';

  // ---- Campaigns CRUD ----

  const getCampaigns = (slug = 'get-campaigns') =>
    sendRequest({ slug, url: route, method: 'GET' });

  const getCampaignById = (campaignId: string) =>
    sendRequest({ slug: `get-campaign-${campaignId}`, url: `${route}/${campaignId}`, method: 'GET', displayToast: false });

  const createCampaign = (payload: ICreateCampaignPayload) =>
    sendRequest({ slug: 'create-campaign', url: route, method: 'POST', payload, displayToast: false });

  const updateCampaign = (campaignId: string, payload: IUpdateCampaignPayload) =>
    sendRequest({ slug: `update-campaign-${campaignId}`, url: `${route}/${campaignId}`, method: 'PATCH', payload, displayToast: false });

  const deleteCampaign = (campaignId: string) =>
    sendRequest({ slug: `delete-campaign-${campaignId}`, url: `${route}/${campaignId}`, method: 'DELETE', displayToast: false });

  // ---- Leads: table ----

  const getCampaignLeads = (
    campaignId: string,
    params?: { status?: string; source?: string; search?: string; page?: number; limit?: number; activityCounts?: number } & ILeadActivityQuery,
    /** `scope` gives the call its own request slot, so it can't abort the table's load (or be aborted by it). */
    options?: { sim?: boolean; scope?: string },
  ) => {
    const qs = params
      ? '?' + Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
      : '';
    const slug = options?.sim
      ? `get-campaign-leads-sim-${campaignId}`
      : `get-campaign-leads-${options?.scope ? `${options.scope}-` : ''}${campaignId}`;
    return sendRequest({ slug, url: `${route}/${campaignId}/leads${qs}`, method: 'GET', displayToast: false });
  };

  // ---- Leads: LeadSearch ----

  /** Starts the fill and resolves with the first batch — the rest lands in the background
   *  and is followed with getLeadFetchStatus. */
  const fetchAndEnrollLeads = (campaignId: string, reset = false) =>
    sendRequest({ slug: `fetch-campaign-leads-${campaignId}`, url: `${route}/${campaignId}/leads/fetch`, method: 'POST', payload: { reset }, displayToast: false });

  /** Runs the LeadSearch query and returns candidates — enrolls nothing. */
  const searchProspects = (campaignId: string, payload: { limit?: number; leadPreferences?: any; exclude?: string[] }) =>
    sendRequest({ slug: `search-prospects-${campaignId}`, url: `${route}/${campaignId}/leads/search-prospects`, method: 'POST', payload, displayToast: false });

  /** Of the given LinkedIn ids, which are currently enrolled in this campaign. */
  const checkEnrolledIds = (campaignId: string, ids: string[]) =>
    sendRequest({ slug: `enrolled-ids-${campaignId}`, url: `${route}/${campaignId}/leads/enrolled-ids`, method: 'POST', payload: { ids }, displayToast: false, displayErrorToast: false });

  /** Enrolls the candidates the user picked out of a search. */
  const enrollFromProspects = (campaignId: string, leads: any[]) =>
    sendRequest({ slug: `enroll-prospects-${campaignId}`, url: `${route}/${campaignId}/leads/from-prospects`, method: 'POST', payload: { leads }, displayToast: false });

  const getLeadFetchStatus = (campaignId: string) =>
    sendRequest({ slug: `lead-fetch-status-${campaignId}`, url: `${route}/${campaignId}/leads/fetch-status`, method: 'GET', displayToast: false, displayErrorToast: false });

  // ---- Leads: LD Contacts ----

  const searchContacts = (campaignId: string, payload: IContactSearchPayload) =>
    sendRequest({ slug: `search-contacts-${campaignId}`, url: `${route}/${campaignId}/leads/search-contacts`, method: 'POST', payload, displayToast: false });

  const enrollFromContacts = (campaignId: string, connectionIds: string[]) =>
    sendRequest({ slug: `enroll-contacts-${campaignId}`, url: `${route}/${campaignId}/leads/from-contacts`, method: 'POST', payload: { connectionIds }, displayToast: false });

  /** Proxy/Unipile — one page of Sales Navigator people search from URL (no saved lists). */
  const searchSalesNavigator = (
    campaignId: string,
    payload: { url?: string; cursor?: string | null; limit?: number; accountId?: string },
  ) =>
    sendRequest({
      slug: `search-sales-navigator-${campaignId}`,
      url: `${route}/${campaignId}/leads/search-sales-navigator`,
      method: 'POST',
      payload,
      displayToast: false,
    });

  /** Proxy/Unipile — one page of LinkedIn post commenters (no extension). */
  const importPostCommenters = (
    campaignId: string,
    payload: {
      postUrl?: string;
      socialId?: string | null;
      cursor?: string | null;
      limit?: number;
      accountId?: string;
    },
  ) =>
    sendRequest({
      slug: `import-post-commenters-${campaignId}`,
      url: `${route}/${campaignId}/leads/import-post-commenters`,
      method: 'POST',
      payload,
      displayToast: false,
    });

  // ---- Leads: CSV ----

  const enrollFromCsv = (campaignId: string, leads: ICsvLeadRow[], options?: { source?: 'csv' | 'posts' | 'sn_lists' | 'signals' }) =>
    sendRequest({
      slug: `enroll-csv-${campaignId}`,
      url: `${route}/${campaignId}/leads/from-csv`,
      method: 'POST',
      payload: { leads, ...(options?.source ? { source: options.source } : {}) },
      displayToast: false,
    });

  /** Persist classic vanity / ACo after SN HTML (or SN profile) resolve. */
  const updateLeadIdentity = (
    campaignId: string,
    leadId: string,
    payload: { publicIdentifier?: string; linkedinId?: string; linkedinUrl?: string; snProfileId?: string },
  ) =>
    sendRequest({
      slug: `update-lead-identity-${campaignId}-${leadId}`,
      url: `${route}/${campaignId}/leads/${leadId}/identity`,
      method: 'PATCH',
      payload,
      displayToast: false,
    });

  // ---- Lead status ----

  const updateLeadStatus = (
    campaignId: string,
    leadId: string,
    status: CampaignLeadStatus,
    currentStepIndex?: number,
    message?: {
      stepType: string;
      content: string;
      direction: 'outbound' | 'inbound' | 'system';
      aiGenerate?: boolean;
      aiGenerated?: boolean;
      language?: string;
      postUrl?: string;
      hasNote?: boolean;
      subject?: string;
      sendMethod?: string;
      creditsUsed?: number;
      conversationId?: string;
    },
    failureReason?: string,
    /** Advance past a post_like / post_comment step that could not run — nothing was sent. */
    skipStep?: boolean,
  ) =>
    sendRequest({
      slug: `update-lead-status-${leadId}`,
      url: `${route}/${campaignId}/leads/${leadId}`,
      method: 'PATCH',
      payload: {
        status,
        ...(currentStepIndex !== undefined && { currentStepIndex }),
        ...(message && { message }),
        ...(failureReason && { failureReason }),
        ...(skipStep && { skipStep: true }),
      },
      displayToast: false,
    });

  const removeLead = (campaignId: string, leadId: string) =>
    sendRequest({ slug: `remove-lead-${leadId}`, url: `${route}/${campaignId}/leads/${leadId}`, method: 'DELETE', displayToast: false, all: true });

  const bulkRemoveLeads = (
    campaignId: string,
    payload: { leadIds?: string[]; all?: boolean; status?: string; source?: string; search?: string } & ILeadActivityQuery,
  ) =>
    sendRequest({
      slug: `bulk-remove-leads-${campaignId}`,
      url: `${route}/${campaignId}/leads/bulk-delete`,
      method: 'POST',
      payload,
      displayToast: false,
      all: true,
    });

  /** Move leads to another campaign. They enroll there as pending, step 0. */
  const moveLeads = (
    campaignId: string,
    payload: {
      targetCampaignId: string;
      leadIds?: string[];
      all?: boolean;
      status?: string;
      source?: string;
      search?: string;
    } & ILeadActivityQuery,
  ) =>
    sendRequest({
      slug: `move-leads-${campaignId}`,
      url: `${route}/${campaignId}/leads/move`,
      method: 'POST',
      payload,
      displayToast: false,
      all: true,
    });

  const retryLead = (campaignId: string, leadId: string) =>
    sendRequest({
      slug: `retry-lead-${leadId}`,
      url: `${route}/${campaignId}/leads/${leadId}/retry`,
      method: 'POST',
      displayToast: false,
      all: true,
    });

  /** Retries failed leads, or suppressed ones taken off the list; with all, status picks which. */
  const bulkRetryLeads = (
    campaignId: string,
    payload: { leadIds?: string[]; all?: boolean; status?: 'failed' | 'suppressed'; source?: string; search?: string } & ILeadActivityQuery,
  ) =>
    sendRequest({
      slug: `bulk-retry-leads-${campaignId}`,
      url: `${route}/${campaignId}/leads/bulk-retry`,
      method: 'POST',
      payload,
      displayToast: false,
      all: true,
    });

  // ---- Suppression lists (profiles, domains, emails) ----
  // Every member can read them and add to them; the API decides who may remove or manage.

  const getSuppressionLists = () =>
    sendRequest({ slug: 'get-suppression-lists', url: `${route}/suppression-lists`, method: 'GET', displayToast: false, all: true });

  const createSuppressionList = (payload: { name: string; scope: SuppressionListScope }) =>
    sendRequest({ slug: 'create-suppression-list', url: `${route}/suppression-lists`, method: 'POST', payload, displayToast: false, all: true });

  /** dryRun: count the leads a wider scope would take out, without saving. */
  const updateSuppressionList = (id: string, payload: { name?: string; scope?: SuppressionListScope; dryRun?: boolean }) =>
    sendRequest({ slug: `update-suppression-list-${id}`, url: `${route}/suppression-lists/${id}`, method: 'PATCH', payload, displayToast: false, all: true });

  const deleteSuppressionList = (id: string) =>
    sendRequest({ slug: `delete-suppression-list-${id}`, url: `${route}/suppression-lists/${id}`, method: 'DELETE', displayToast: false, all: true });

  /** One list's entries; the workspace list when listId is empty. */
  const getSuppressions = (params: { listId?: string; type?: SuppressionType | ''; search?: string; page?: number; limit?: number }) =>
    sendRequest({ slug: 'get-suppressions', url: `${route}/suppressions`, method: 'GET', params, displayToast: false, all: true });

  /** What adding these values would do — nothing is saved. */
  const previewSuppressions = (payload: { listId?: string; type: SuppressionType; values: string[] }) =>
    sendRequest({ slug: 'preview-suppressions', url: `${route}/suppressions/preview`, method: 'POST', payload, displayToast: false, all: true });

  /** Adds the values and takes matching leads out of the campaigns the list binds, in the same call. */
  const addSuppressions = (payload: { listId?: string; type: SuppressionType; values: string[]; note?: string; source?: 'manual' | 'lead' }) =>
    sendRequest({ slug: 'add-suppressions', url: `${route}/suppressions`, method: 'POST', payload, displayToast: false, all: true });

  /** Domain entries only: also match by company name. dryRun resolves the name and counts the reach. */
  const updateSuppression = (id: string, payload: { matchCompanyName: boolean; companyName?: string; dryRun?: boolean }) =>
    sendRequest({ slug: `update-suppression-${id}`, url: `${route}/suppressions/${id}`, method: 'PATCH', payload, displayToast: false, all: true });

  const removeSuppression = (id: string) =>
    sendRequest({ slug: `remove-suppression-${id}`, url: `${route}/suppressions/${id}`, method: 'DELETE', displayToast: false, all: true });

  /** Queue skipped_not_open leads for paid InMail via cloud executor (same campaign). */
  const bulkResendInMail = (
    campaignId: string,
    payload: { leadIds?: string[]; all?: boolean } & ILeadActivityQuery,
  ) =>
    sendRequest({
      slug: `bulk-resend-inmail-${campaignId}`,
      url: `${route}/${campaignId}/leads/bulk-resend-inmail`,
      method: 'POST',
      payload,
      displayToast: false,
      all: true,
    });

  const bulkUpdateLeadStatus = (
    campaignId: string,
    payload: {
      leadIds?: string[];
      all?: boolean;
      status: CampaignLeadStatus;
      filterStatus?: string;
      source?: string;
      search?: string;
    } & ILeadActivityQuery,
  ) =>
    sendRequest({
      slug: `bulk-status-leads-${campaignId}`,
      url: `${route}/${campaignId}/leads/bulk-status`,
      method: 'POST',
      payload,
      displayToast: false,
      all: true,
    });

  const generateCampaignMessage = (
    campaignId: string,
    leadId: string,
    payload: { stepType: string; content: string; language: string; postText?: string }
  ) =>
    sendRequest({
      slug: `generate-campaign-message-${leadId}`,
      url: `${route}/${campaignId}/leads/${leadId}/generate-message`,
      method: 'POST',
      payload,
      displayToast: false,
    });

  const updateLeadContentOverride = (
    campaignId: string,
    leadId: string,
    payload: { stepIndex: number; content: string | null },
  ) =>
    sendRequest({
      slug: `update-lead-content-override-${leadId}`,
      url: `${route}/${campaignId}/leads/${leadId}/content-override`,
      method: 'PATCH',
      payload,
      displayToast: false,
    });

  const previewCampaignAiMessage = (payload: {
    stepType: 'message' | 'post_comment' | 'connection_request' | 'email' | 'email_subject';
    content: string;
    language: string;
    postText?: string;
    campaignDescription?: string;
    variableFallbacks?: ICampaignVariableFallbacks;
  }) =>
    sendRequest({
      // Per step type: two email fields previewing at once must not abort each other,
      // and sendRequest cancels any in-flight request sharing a slug.
      slug: `preview-campaign-ai-message-${payload.stepType}`,
      url: `${route}/ai-preview`,
      method: 'POST',
      payload,
      displayToast: false,
      // `all: true` so a rejection keeps its message. Without it a 400 came back as
      // null and every failure read "Could not generate preview", which hid the one
      // thing worth knowing — that the server refused the request and why.
      all: true,
    });

  /** An image placed in a rich-text email body. Stored in our bucket, never inlined. */
  const uploadEmailImage = (file: File) => {
    const form = new FormData();
    form.append('image', file);
    return sendRequest({
      slug: 'upload-email-image',
      url: `${route}/email-image`,
      method: 'POST',
      payload: form,
      // Axios strips this and sets the real multipart boundary itself.
      additionalHeaders: { 'Content-Type': 'multipart/form-data' },
      displayToast: false,
      all: true,
    });
  };

  // ---- Connection check (LinkedIn 1st via LD Contacts) ----

  const checkLeadConnection = (campaignId: string, leadId: string) =>
    sendRequest({
      slug: `lead-connection-check-${leadId}`,
      url: `${route}/${campaignId}/leads/${leadId}/connection-check`,
      method: 'GET',
      displayToast: false,
    });

  // ---- Extension daily/weekly limit check ----

  const checkExtensionStepLimit = (campaignId: string, stepType: string) =>
    sendRequest({
      slug: `extension-step-check-${campaignId}-${stepType}`,
      url: `${route}/${campaignId}/extension-step-check?stepType=${encodeURIComponent(stepType)}`,
      method: 'GET',
      displayToast: false,
    });

  /** Monthly CR note quota (free LinkedIn = 5/month). Fail-closed if Premium unknown. */
  const checkConnectionRequestNote = (
    campaignId: string,
    payload: {
      noteContent?: string;
      unipileAccountId?: string;
      isPremium?: boolean;
      /** Persist LinkedIn CUSTOM_INVITE_LIMIT_REACHED for the current calendar month. */
      markLinkedInNotesExhausted?: boolean;
    } = {},
  ) =>
    sendRequest({
      slug: `connection-request-note-check-${campaignId}`,
      url: `${route}/${campaignId}/connection-request-note-check`,
      method: 'POST',
      payload,
      displayToast: false,
    });

  // ---- Proxy execution ----

  const executeStep = (campaignId: string, leadId: string, payload: IExecuteStepPayload) =>
    sendRequest({
      slug: `execute-step-${leadId}`,
      url: `${route}/${campaignId}/leads/${leadId}/execute-step`,
      method: 'POST',
      payload,
      displayToast: false,
    });

  const updateProxyConfig = async (campaignId: string, payload: IUpdateProxyConfigPayload) => {
    const res = await sendRequest({
      slug: `update-proxy-config-${campaignId}`,
      url: `${route}/${campaignId}/proxy-config`,
      method: 'PATCH',
      payload,
      displayToast: false,
      displayErrorToast: true,
      all: true,
    }) as IResponse | undefined;

    if (!res?.success) return null;
    return res.data;
  };

  /**
   * Has LinkedIn confirmed an in-app approval yet? Asked in the background while the
   * verification box is open, so the account flips to connected the moment the phone
   * approval lands, without anyone clicking.
   *
   * Separate from updateProxyConfig on purpose: its own slug, so a background check
   * and a click on the button never cancel each other; and no error toast, because a
   * question asked every few seconds must not put a red banner up each time one
   * attempt fails. Resolves to the same { status, accountId, checkpointType?, reason? }.
   */
  const checkProxyCheckpoint = async (campaignId: string, accountId: string) => {
    const res = await sendRequest({
      slug: `check-proxy-checkpoint-${campaignId}-${accountId}`,
      url: `${route}/${campaignId}/proxy-config`,
      method: 'PATCH',
      payload: { accountId },
      displayToast: false,
      all: true,
    }) as IResponse | undefined;
    return res?.success ? res.data : null;
  };

  /** `scope` keeps callers on the same page (header banner, Leads tab) from aborting each other. */
  const getProxyAccounts = (campaignId: string, scope = 'default') =>
    sendRequest({
      slug: `get-proxy-accounts-${scope}-${campaignId}`,
      url: `${route}/${campaignId}/proxy-accounts`,
      method: 'GET',
      displayToast: false,
    });

  // Existing workspace accounts that can be reused (added without re-entering credentials).
  // Workspace-scoped so it also works during campaign creation (no campaign yet).
  const getWorkspaceAccounts = (excludeCampaignId?: string) =>
    sendRequest({
      slug: 'get-workspace-accounts',
      url: `${route}/workspace-accounts`,
      method: 'GET',
      params: excludeCampaignId ? { excludeCampaignId } : undefined,
      displayToast: false,
    });

  /** Set or clear a lead's email by hand. Pass '' to clear. No credits involved. */
  const updateLeadEmail = (campaignId: string, leadId: string, email: string, emailType?: 'business' | 'private') =>
    sendRequest({
      slug: `update-lead-email-${leadId}`,
      url: `${route}/${campaignId}/leads/${leadId}/email`,
      method: 'PATCH',
      payload: { email, emailType },
      displayToast: false,
      all: true,
    });

  // ---- Sender identities (LinkedIn + mailbox, paired) ----

  /**
   * `live` re-checks each mailbox against the provider — slower, use on the settings screen.
   * `campaignId` narrows the list to the senders that campaign actually sends as.
   */
  /**
   * Senders — the whole workspace, or just one campaign's.
   *
   * The slug carries the scope on purpose. sendRequest cancels any in-flight request
   * sharing a slug, and these two shapes are asked for concurrently all the time: the
   * campaign panel refreshing while the settings dialog loads, or either of them while
   * the mailbox-connect poll runs every 2.5s. On one shared slug they cancelled each
   * other, which is how a mailbox connected in settings still read as "not connected"
   * on the campaign until the page was reloaded.
   */
  /*
    `scope` separates two callers asking the SAME question at the same time.

    Settings opens over the campaign, so CampaignSenderIdentities and
    SendingAccountsSection are both mounted and both want the workspace list. Sharing a
    slug means whichever fires second cancels the first, and the loser reads an empty
    response — which is how closing Settings could leave the campaign's account picker
    with nothing to pick from.
  */
  /** `credits` (workspace list + live) adds InMail balances — one more provider call per account. */
  const getSenderIdentities = (live = false, campaignId?: string, scope = 'default', credits = false) =>
    sendRequest({
      slug: `get-sender-identities-${scope}-${campaignId ?? 'workspace'}-${live ? 'live' : 'cached'}`,
      url: `${route}/identities`,
      method: 'GET',
      params: {
        ...(live ? { live: 'true' } : {}),
        ...(campaignId ? { campaignId } : {}),
        ...(credits ? { credits: 'true' } : {}),
      },
      displayToast: false,
    });

  /** Mailboxes this workspace may reuse. Pass the identity being edited to exclude its own. */
  const getAvailableMailboxes = (identityId?: string) =>
    sendRequest({
      slug: 'get-available-mailboxes',
      url: `${route}/identities/available-mailboxes`,
      method: 'GET',
      params: identityId ? { identityId } : undefined,
      displayToast: false,
    });

  /** Promote an already-connected Unipile LinkedIn account into a sender identity. */
  const createIdentityFromAccount = (unipileAccountId: string) =>
    sendRequest({
      slug: 'create-identity-from-account',
      url: `${route}/identities/from-account`,
      method: 'POST',
      payload: { unipileAccountId },
      displayToast: false,
    });

  /**
   * Identity for a user whose LinkedIn steps run in the browser extension.
   * `all: true` so the caller gets the server's error message — this endpoint fails
   * with something the user can act on ("open LeadDelta on LinkedIn once"), which
   * the default unwrapping would throw away.
   */
  const createExtensionIdentity = () =>
    sendRequest({
      slug: 'create-extension-identity',
      url: `${route}/identities/extension`,
      method: 'POST',
      displayToast: false,
      all: true,
    });

  /**
   * Gmail/Outlook need an OAuth consent screen — returns a hosted link to open.
   * `returnTo` is the same-site path the provider sends the user back to, so they
   * land on the screen they left instead of the campaigns list.
   */
  /**
   * Hosted-auth link for connecting a mailbox.
   *
   * `mailbox=connected` is stamped onto the return URL here rather than left to each
   * caller. The consent screen redirects INSIDE the 520px popup, and that flag is how
   * the app recognises it is running in the popup and closes itself instead of
   * rendering the whole of LeadDelta in a sliver. Every caller was passing a plain
   * URL without it, so the popup stayed open showing a squeezed copy of the app while
   * the real window looked untouched.
   */
  const createMailConnectLink = (identityId: string, returnTo?: string) => {
    const withFlag = (() => {
      if (!returnTo) return returnTo;
      try {
        const url = new URL(returnTo, window.location.origin);
        url.searchParams.set('mailbox', 'connected');
        // Same-origin: return a relative URL so the provider's allow-list is not a factor.
        return url.origin === window.location.origin
          ? `${url.pathname}${url.search}${url.hash}`
          : url.toString();
      } catch {
        // Not parseable — send it on untouched rather than dropping the round-trip.
        return returnTo;
      }
    })();

    return sendRequest({
      slug: `mail-connect-link-${identityId}`,
      url: `${route}/identities/${identityId}/mail-link`,
      method: 'POST',
      payload: { returnTo: withFlag },
      displayToast: false,
    });
  };

  /**
   * Connect a mailbox from entered credentials and pair it with the identity.
   * `all: true` so the caller can show the provider's real failure reason.
   */
  const connectMailbox = (identityId: string, payload: IConnectMailboxPayload) =>
    sendRequest({
      slug: `connect-mailbox-${identityId}`,
      url: `${route}/identities/${identityId}/mail/connect`,
      method: 'POST',
      payload,
      displayToast: false,
      all: true,
    });

  /** Re-authenticate an expired mailbox in place (keeps warmup + bounce history). */
  const reconnectMailbox = (identityId: string, payload: Omit<IConnectMailboxPayload, 'email'> & { email?: string }) =>
    sendRequest({
      slug: `reconnect-mailbox-${identityId}`,
      url: `${route}/identities/${identityId}/mail/reconnect`,
      method: 'POST',
      payload,
      displayToast: false,
      all: true,
    });

  const attachMailbox = (identityId: string, unipileAccountId: string) =>
    sendRequest({
      slug: `attach-mailbox-${identityId}`,
      url: `${route}/identities/${identityId}/mail`,
      method: 'POST',
      payload: { unipileAccountId },
      displayToast: false,
    });

  /**
   * Remove a mailbox from a sender. It is disconnected on the provider by default —
   * pass `keepOnProvider` only when the account should stay linked.
   */
  const detachMailbox = (identityId: string, keepOnProvider = false) =>
    sendRequest({
      slug: `detach-mailbox-${identityId}`,
      url: `${route}/identities/${identityId}/mail`,
      method: 'DELETE',
      params: keepOnProvider ? { keepOnProvider: 'true' } : undefined,
      displayToast: false,
      // `all: true` so the caller sees providerOutcome — a provider that refuses the
      // delete would otherwise be indistinguishable from a clean removal.
      all: true,
    });

  /**
   * Connect a LinkedIn account to the workspace, with no campaign involved. Answers
   * { status: 'connected' | 'checkpoint' | 'failed', accountId?, checkpointType?, reason? }.
   */
  const connectWorkspaceLinkedIn = (payload: ILinkedInLoginFields & { country?: string; reconnectAccountId?: string }) =>
    sendRequest({
      slug: 'connect-workspace-linkedin',
      url: `${route}/identities/connect-linkedin`,
      method: 'POST',
      payload,
      displayToast: false,
      all: true,
    });

  /**
   * Finish a workspace LinkedIn connection that stopped on a verification step.
   * `code` is omitted for IN_APP_VALIDATION, where approval happens in the app.
   */
  const solveWorkspaceLinkedInCheckpoint = (payload: { accountId: string; code?: string }) =>
    sendRequest({
      slug: 'workspace-linkedin-checkpoint',
      url: `${route}/identities/linkedin-checkpoint`,
      method: 'POST',
      payload,
      displayToast: false,
      all: true,
    });

  /**
   * The background version of the in-app approval check for a workspace connection —
   * see checkProxyCheckpoint. Its own slug, so the watcher and a click on the button
   * never cancel one another.
   */
  const checkWorkspaceLinkedInCheckpoint = (accountId: string) =>
    sendRequest({
      slug: `check-workspace-linkedin-checkpoint-${accountId}`,
      url: `${route}/identities/linkedin-checkpoint`,
      method: 'POST',
      payload: { accountId },
      displayToast: false,
      all: true,
    });

  /**
   * Whether this sender is offered when a NEW campaign is built. Campaigns already
   * running keep using it either way — nothing in the send path reads this.
   */
  const setSenderActive = (identityId: string, active: boolean) =>
    sendRequest({
      slug: `sender-active-${identityId}`,
      url: `${route}/identities/${identityId}/active`,
      method: 'PATCH',
      payload: { active },
      displayToast: false,
      all: true,
    });

  /**
   * Delete a sender for good, freeing the LinkedIn seat it holds.
   *
   * Distinct from setSenderActive(false), which only hides the sender from NEW
   * campaigns — the account stays connected and keeps its seat. Refused by the server
   * while a live campaign still sends as this person.
   */
  const deleteSender = (identityId: string) =>
    sendRequest({
      slug: `delete-sender-${identityId}`,
      url: `${route}/identities/${identityId}`,
      method: 'DELETE',
      displayToast: false,
      all: true,
    });

  /**
   * The sender's pasted signature. Sent as-is — the server sanitizes it and rehosts
   * any inline base64 images, and answers with what it actually stored, so the editor
   * can show the cleaned version rather than the raw paste.
   */
  const setSenderSignature = (identityId: string, signature: Partial<ISenderSignature>) =>
    sendRequest({
      slug: `sender-signature-${identityId}`,
      url: `${route}/identities/${identityId}/signature`,
      method: 'PATCH',
      payload: { signature },
      displayToast: false,
      all: true,
    });

  /** Per-mailbox daily send limit. 0 hands it back to the warmup ramp. */
  const setMailDailyLimit = (identityId: string, dailyLimit: number) =>
    sendRequest({
      slug: `mail-daily-limit-${identityId}`,
      url: `${route}/identities/${identityId}/mail/limit`,
      method: 'PATCH',
      payload: { dailyLimit },
      displayToast: false,
      all: true,
    });

  /** Clear an auto-pause after the user has cleaned up their list. */
  const resumeMailbox = (identityId: string) =>
    sendRequest({
      slug: `resume-mailbox-${identityId}`,
      url: `${route}/identities/${identityId}/mail/resume`,
      method: 'POST',
      displayToast: false,
    });

  const setCampaignIdentities = (campaignId: string, identityIds: string[]) =>
    sendRequest({
      slug: `set-campaign-identities-${campaignId}`,
      url: `${route}/${campaignId}/identities`,
      method: 'PATCH',
      payload: { identityIds },
      displayToast: false,
    });

  // ---- Email pre-flight ----

  /** Pre-activation check: is every sender able to run this sequence? */
  const getEmailValidation = (campaignId: string) =>
    sendRequest({
      slug: `email-validation-${campaignId}`,
      url: `${route}/${campaignId}/email-validation`,
      method: 'GET',
      displayToast: false,
    });

  /** Coverage report — a quote, never a charge. */
  const getEmailPreflight = (campaignId: string, preference?: string) =>
    sendRequest({
      slug: `email-preflight-${campaignId}`,
      url: `${route}/${campaignId}/email-preflight`,
      method: 'GET',
      params: preference ? { preference } : undefined,
      displayToast: false,
    });

  /**
   * Spend the credits and start enrichment. `confirm: true` is required — without it
   * the endpoint returns the quote again rather than charging.
   */
  const runEmailPreflight = (campaignId: string, preference?: string, confirm = false) =>
    sendRequest({
      slug: `run-email-preflight-${campaignId}`,
      url: `${route}/${campaignId}/email-preflight`,
      method: 'POST',
      payload: { preference, confirm },
      displayToast: false,
    });

  // ---- Workspace tags (for contacts filter) ----

  const getWorkspaceTags = () =>
    sendRequest({ slug: 'get-workspace-tags-for-campaigns', url: '/tags', method: 'GET', displayToast: false });

  // ---- Performance analytics ----

  const getWorkspacePerformance = (params: IPerformanceQuery) =>
    sendRequest({ slug: 'get-workspace-performance', url: `${route}/analytics/workspace`, method: 'GET', params: performanceParams(params), displayToast: false });

  const getSendersPerformance = (params: IPerformanceQuery) =>
    sendRequest({ slug: 'get-senders-performance', url: `${route}/analytics/senders`, method: 'GET', params: performanceParams(params), displayToast: false });

  return {
    getCampaigns, getCampaignById, createCampaign, updateCampaign, deleteCampaign,
    getCampaignLeads, fetchAndEnrollLeads, getLeadFetchStatus, searchProspects, enrollFromProspects, checkEnrolledIds,
    searchContacts, enrollFromContacts, searchSalesNavigator, importPostCommenters,
    enrollFromCsv, updateLeadIdentity,
    updateLeadStatus, removeLead, bulkRemoveLeads, moveLeads, retryLead, bulkRetryLeads, bulkResendInMail, bulkUpdateLeadStatus, generateCampaignMessage, updateLeadContentOverride, updateLeadEmail, previewCampaignAiMessage, uploadEmailImage,
    checkExtensionStepLimit, checkConnectionRequestNote, checkLeadConnection,
    executeStep, updateProxyConfig, checkProxyCheckpoint, getProxyAccounts, getWorkspaceAccounts,
    getSenderIdentities, getAvailableMailboxes, createIdentityFromAccount, createExtensionIdentity,
    createMailConnectLink, connectMailbox, reconnectMailbox, attachMailbox, detachMailbox,
    resumeMailbox, setMailDailyLimit, setSenderSignature, setSenderActive, deleteSender, connectWorkspaceLinkedIn, solveWorkspaceLinkedInCheckpoint, checkWorkspaceLinkedInCheckpoint, setCampaignIdentities,
    getEmailValidation, getEmailPreflight, runEmailPreflight,
    getWorkspaceTags,
    getWorkspacePerformance, getSendersPerformance,
    getSuppressionLists, createSuppressionList, updateSuppressionList, deleteSuppressionList,
    getSuppressions, previewSuppressions, addSuppressions, updateSuppression, removeSuppression,
  };
};
